import {
  AppState,
  Auth0Provider,
  Auth0ProviderOptions,
  useAuth0,
} from '@auth0/auth0-react'
import { useNavigate } from '@tanstack/react-router'
import { useEnvironmentVariables, useRuntimeVariables } from 'app-kit'
import { MessageBanner } from 'components/MessageBanner'
import React, { useState } from 'react'

import { LoginPage } from './LoginPage'

type ContentSelectorProps = {
  children: JSX.Element
  /**
   * The component that shows up when authentication is happening
   */
  LoadingComponent?: React.ElementType
}

type AuthProviderType = {
  children: JSX.Element
  clientId: string
  environment?: string
  basePath?: string
} & ContentSelectorProps &
  Partial<Auth0ProviderOptions>

export const Auth0Content = ({
  children,
  LoadingComponent,
}: ContentSelectorProps) => {
  const { isLoading, isAuthenticated, error, loginWithRedirect } = useAuth0()

  if (isLoading) {
    if (LoadingComponent) {
      return <LoadingComponent />
    }
    return null
  }

  if (!isAuthenticated) {
    // automatically send to Auth0 unless there's an error
    if (!error) {
      loginWithRedirect({
        appState: { returnTo: window.location.href },
      })
    }

    return <LoginPage provider='auth0' />
  }
  return children
}

export const Auth0MessageBanner = () => {
  const { error } = useAuth0()

  const [showBanner, setShowBanner] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  React.useEffect(() => {
    if (error) {
      setErrorMessage(`Error: ${error.message}`)
      setShowBanner(true)
    } else {
      setErrorMessage('')
      setShowBanner(false)
    }
  }, [error])

  return (
    <MessageBanner
      absolutePositionTop
      showBanner={showBanner}
      hideBanner={() => setShowBanner(false)}
      contents={
        <div>
          <div id={'banner-error-message'}>{errorMessage}</div>
          <div id={'banner-error-details'}>
            Having trouble logging in? Contact{' '}
            <a id={'banner-error-link'} href='mailto:support@geckorobotics.com'>
              support@geckorobotics.com
            </a>
          </div>
        </div>
      }
    />
  )
}

export const AuthProvider = ({
  children,
  clientId,
  basePath,
  LoadingComponent,
  ...rest
}: AuthProviderType) => {
  const navigate = useNavigate()
  const { IS_PROD } = useEnvironmentVariables()
  const { AUTH0_AUDIENCE } = useRuntimeVariables()
  const AUTH0_DOMAIN = IS_PROD
    ? 'login.geckorobotics.com'
    : 'login.dev.geckorobotics.com'

  return (
    <Auth0Provider
      cacheLocation='localstorage'
      clientId={clientId}
      domain={AUTH0_DOMAIN}
      onRedirectCallback={(appState?: AppState) => {
        const returnTo = appState?.returnTo ?? window.location.pathname

        try {
          const url = new URL(returnTo, window.location.origin)
          const pathname = url.pathname.replace('/forecast', '')
          const searchParams = new URLSearchParams(url.search)

          const search: Record<string, string> = {}
          searchParams.forEach((value, key) => {
            search[key] = value
          })

          navigate({
            to: pathname,
            search:
              Object.keys(search).length > 0
                ? (prev) => ({ ...prev, ...search })
                : undefined,
          })
        } catch (parseError) {
          console.error(
            'Error parsing url redirecting to home page',
            parseError
          )
          const pathname = returnTo.replace('/forecast', '')
          navigate({ to: pathname })
        }
      }}
      authorizationParams={{
        redirect_uri: new URL(basePath ? basePath : '', window.location.origin)
          .href,
        audience: AUTH0_AUDIENCE,
        domain: AUTH0_DOMAIN,
        ...(rest.authorizationParams && { ...rest.authorizationParams }),
      }}
      {...rest}>
      <Auth0MessageBanner />
      <Auth0Content LoadingComponent={LoadingComponent}>
        {children}
      </Auth0Content>
    </Auth0Provider>
  )
}
